import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useRef, useState } from 'react';
import { Layout } from '@/components/layout';
import IconOfficeBuilding from '@/assets/icons/heroIcons/OfficeBuilding.svg';
import IconMarker from '@/assets/icons/heroIcons/LocationMarker.svg';
import { LINKS } from '@/constants/links';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { usePageView } from '@/analytics/pageTypes';
import { JobDetail } from '@/types/job';
import { Faq } from '@/sections/career/Faq';
import { MoreInfo } from '@/sections/career/MoreInfo';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { Button } from '@/components/buttons';
import { Contact } from '@/sections/careerDetail/Contact';
import { HowItWorks } from '@/sections/career/HowItWorks';
import { Share } from '@/sections/careerDetail/Share';
import { Box } from '@/sections/pricing/Box';
import { HeroWithBreadcrumbs } from '@/sections/HeroWithBreadcrumbs';
import { BoxRow } from '@/sections/pricing/BoxRow';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/links';
import { SectionHeadline } from '@/components/SectionHeadline';
import { motion } from 'framer-motion';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { MarkdownContent } from '@/components/Markdown/Markdown';
import { getJobDetail, getJobDetailStaticPaths } from '@/sections/jobs/functions/getJobDetail';
import { Department } from '@/sections/jobs/Department';
import { Model } from '@/sections/jobs/Model';
import { LegalRelationship } from '@/sections/jobs/LegalRelationship';
import { Employment } from '@/sections/jobs/Employment';
import { Duration } from '@/sections/jobs/Duration';
import { MinimalEducation } from '@/sections/jobs/MinimalEducation';
interface PageProps extends IntlProps {
  jobDetail: JobDetail;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(121);
  const {
    jobDetail,
    menuPromoBanners
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["career", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const t1 = jobDetail?.name || "";
  let t2;
  if ($[1] !== jobDetail?.slug.current) {
    t2 = LINKS().careerDetail(jobDetail?.slug.current || "");
    $[1] = jobDetail?.slug.current;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      title: t1,
      url: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  usePageView("career-detail", t3);
  const contactFormRef = useRef(null);
  const [selectedInterest, setSelectedInterest] = useState(undefined);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = interest => {
      if (contactFormRef.current) {
        if (interest) {
          setSelectedInterest(true);
        }
        contactFormRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleScrollToContact = t4;
  let t5;
  if ($[7] !== t) {
    t5 = function CtaButton() {
      return <Button analyticsParam="careerDetailCta" testId="careerDetailCta" onClick={handleScrollToContact} align="center" variant="primary" size="lg">{t("detail.cta")}</Button>;
    };
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const CtaButton = t5;
  const title = jobDetail?.name;
  let t6;
  if ($[9] !== t || $[10] !== title) {
    t6 = t("seoDetail.title", {
      title
    });
    $[9] = t;
    $[10] = title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("seoDetail.description");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <SeoHeader title={t6} description={t7} />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("jobVacancy.title");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = LINKS();
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = {
      title: t9,
      to: t10.careerList
    };
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("detail.title");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = {
      title: t12
    };
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11 || $[27] !== t13) {
    t14 = [t11, t13];
    $[26] = t11;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = jobDetail?.department;
  let t16;
  if ($[29] !== t15) {
    t16 = <Department value={t15} />;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== title) {
    t17 = <SectionHeadline variant="inverted" className="mb-4 lg:mb-6" anchorKey="career.detail.title">{title}</SectionHeadline>;
    $[31] = title;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== jobDetail) {
    t18 = jobDetail?.model && <div className="flex flex-row items-center justify-center gap-1"><IconMarker className="h-4 w-4 md:h-6 md:w-6" /><p><Model value={jobDetail.model} /></p></div>;
    $[33] = jobDetail;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <IconOfficeBuilding className="h-4 w-4 md:h-6 md:w-6" />;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = jobDetail?.addressShort;
  let t21;
  if ($[36] !== t20) {
    t21 = <div className="flex flex-row items-center justify-center gap-1">{t19}<p>{t20}</p></div>;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t18 || $[39] !== t21) {
    t22 = <div className="flex flex-col items-center justify-center gap-2 text-sm text-white md:flex-row lg:gap-6 lg:text-base">{t18}{t21}</div>;
    $[38] = t18;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t) {
    t23 = t("detail.intro");
    $[41] = t;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t23) {
    t24 = <Text variant="p2" className="text-light-gray-100 my-10 text-center lg:my-12">{t23}</Text>;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== CtaButton) {
    t25 = <CtaButton />;
    $[45] = CtaButton;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t14 || $[48] !== t16 || $[49] !== t17 || $[50] !== t22 || $[51] !== t24 || $[52] !== t25) {
    t26 = <HeroWithBreadcrumbs breadcrumbs={t14} label={t16} circleClassName="top-0 right-0 hidden lg:block w-[55rem] h-[55rem] translate-x-1/3 -translate-y-1/3 -rotate-90" contentClassName="max-w-[62.5rem]">{t17}{t22}{t24}{t25}</HeroWithBreadcrumbs>;
    $[47] = t14;
    $[48] = t16;
    $[49] = t17;
    $[50] = t22;
    $[51] = t24;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <SectionDelimiter color="bg-blue-100" background="bg-blue-900" />;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== jobDetail) {
    t28 = jobDetail?.descriptionMarkdown && <MarkdownContent content={jobDetail.descriptionMarkdown} />;
    $[55] = jobDetail;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t) {
    t29 = t("detail.positionInfo.title");
    $[57] = t;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t29) {
    t30 = <p className="text-xl font-semibold text-blue-500">{t29}</p>;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== jobDetail || $[62] !== t) {
    t31 = jobDetail?.address && <BoxRow left={t("detail.positionInfo.address")} right={<Link testId="officeMap" analyticsParam="officeMap" to={`https://www.google.cz/maps/place/${jobDetail.address}`} target="_blank">{jobDetail.address}</Link>} />;
    $[61] = jobDetail;
    $[62] = t;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== jobDetail || $[65] !== t) {
    t32 = jobDetail?.model && <BoxRow left={t("detail.positionInfo.model")} right={<Model value={jobDetail.model} />} />;
    $[64] = jobDetail;
    $[65] = t;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== jobDetail || $[68] !== t) {
    t33 = jobDetail?.department && <BoxRow left={t("detail.positionInfo.department")} right={<Department value={jobDetail.department} />} />;
    $[67] = jobDetail;
    $[68] = t;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== jobDetail || $[71] !== t) {
    t34 = jobDetail?.legalRelationship && <BoxRow left={t("detail.positionInfo.legalRelationship")} right={<LegalRelationship value={jobDetail.legalRelationship} />} />;
    $[70] = jobDetail;
    $[71] = t;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== jobDetail || $[74] !== t) {
    t35 = jobDetail?.employment && <BoxRow left={t("detail.positionInfo.employment")} right={<Employment value={jobDetail.employment} />} />;
    $[73] = jobDetail;
    $[74] = t;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== jobDetail || $[77] !== t) {
    t36 = jobDetail?.duration && <BoxRow left={t("detail.positionInfo.duration")} right={<Duration value={jobDetail.duration} />} />;
    $[76] = jobDetail;
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== jobDetail || $[80] !== t) {
    t37 = jobDetail?.minimalEducation && <BoxRow left={t("detail.positionInfo.minimalEducation")} right={<MinimalEducation value={jobDetail.minimalEducation} />} />;
    $[79] = jobDetail;
    $[80] = t;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== jobDetail || $[83] !== t) {
    t38 = jobDetail?.requiredLanguageSkills && <BoxRow left={t("detail.positionInfo.requiredLanguageSkills")} right={jobDetail.requiredLanguageSkills} />;
    $[82] = jobDetail;
    $[83] = t;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== t30 || $[86] !== t31 || $[87] !== t32 || $[88] !== t33 || $[89] !== t34 || $[90] !== t35 || $[91] !== t36 || $[92] !== t37 || $[93] !== t38) {
    t39 = <Box>{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}</Box>;
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
    $[91] = t36;
    $[92] = t37;
    $[93] = t38;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  let t40;
  if ($[95] !== CtaButton) {
    t40 = <CtaButton />;
    $[95] = CtaButton;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== jobDetail?.slug.current) {
    t41 = LINKS().careerDetail(jobDetail?.slug.current || "");
    $[97] = jobDetail?.slug.current;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== t41) {
    t42 = <Share url={t41} />;
    $[99] = t41;
    $[100] = t42;
  } else {
    t42 = $[100];
  }
  let t43;
  if ($[101] !== t28 || $[102] !== t39 || $[103] !== t40 || $[104] !== t42) {
    t43 = <div className="relative bg-blue-100 px-4 pt-10 pb-20 lg:pt-20 lg:pb-40"><div className="mx-auto flex max-w-192 flex-col gap-2">{t28}{t39}{t40}{t42}</div></div>;
    $[101] = t28;
    $[102] = t39;
    $[103] = t40;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <SectionDelimiter color="bg-blue-900" background="bg-blue-100" />;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  let t46;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="relative bg-blue-900 pt-10 pb-20 lg:pt-20 lg:pb-40"><HowItWorks /></div>;
    t46 = <SectionDelimiter color="bg-blue-700" background="bg-blue-900" />;
    $[107] = t45;
    $[108] = t46;
  } else {
    t45 = $[107];
    t46 = $[108];
  }
  let t47;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="bgCircle absolute top-1/3 right-0 -translate-x-1/2 -translate-y-1/2 lg:top-1/4" />;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  const t48 = title ?? undefined;
  let t49;
  if ($[110] !== selectedInterest || $[111] !== t48) {
    t49 = <motion.div className="relative bg-blue-700 pt-10 pb-20 lg:pt-20 lg:pb-40" ref={contactFormRef}>{t47}<Contact job={t48} initialInterest={selectedInterest} /></motion.div>;
    $[110] = selectedInterest;
    $[111] = t48;
    $[112] = t49;
  } else {
    t49 = $[112];
  }
  let t50;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <SectionDelimiter color="bg-blue-50" background="bg-blue-700" />;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <div className="relative overflow-hidden bg-blue-50 pt-10 pb-14 lg:pt-20 lg:pb-26"><div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Faq /><MoreInfo /></div><div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" /></div>;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== menuPromoBanners || $[116] !== t26 || $[117] !== t43 || $[118] !== t49 || $[119] !== t8) {
    t52 = <Layout variant="inverted" menuPromoBanners={menuPromoBanners}>{t8}{t26}{t27}{t43}{t44}{t45}{t46}{t49}{t50}{t51}</Layout>;
    $[115] = menuPromoBanners;
    $[116] = t26;
    $[117] = t43;
    $[118] = t49;
    $[119] = t8;
    $[120] = t52;
  } else {
    t52 = $[120];
  }
  return t52;
}
export const getStaticPaths = async () => {
  return await getJobDetailStaticPaths();
};
export const getStaticProps = (async ctx => {
  const jobSlug = ctx.params?.job as string;
  const jobDetail = await getJobDetail(jobSlug);
  if (!jobDetail) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      jobDetail,
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['career']))
    }
  };
}) satisfies GetStaticProps<PageProps>;